import { type Instant, parseInstant } from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey, principalKey } from '../http/keys.ts'
import type { Capability, LedgerRole } from './session.ts'

export interface Invitation {
  readonly id: string
  readonly ledgerId: string
  readonly invitedSubject: string
  readonly role: LedgerRole
  readonly status: string
  readonly expiresAt: Instant
}

export interface LedgerMembership {
  readonly ledgerId: string
  readonly role: LedgerRole
  readonly capabilities: readonly Capability[]
}

export const invitationKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'invitations'),
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'invitations')],
  /** Accepting an invitation adds a ledger the principal did not have before. */
  invalidatedByAccept: (): Invalidations => [principalKey('ledgers')],
} as const

export interface InvitationsRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly Invitation[]>
  create(ledgerId: string, input: Schemas['InviteRequest']): Promise<Invitation>
  revoke(ledgerId: string, invitationId: string): Promise<void>
  accept(token: string): Promise<LedgerMembership>
}

export function createInvitationsRepository(client: TalerClient): InvitationsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/invitations`

  return {
    async list(ledgerId, signal) {
      const response = await client.call<Schemas['LedgerInvitationResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toInvitation)
    },

    async create(ledgerId, input) {
      return toInvitation(
        await client.call<Schemas['LedgerInvitationResponse'], Schemas['InviteRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: input,
        }),
      )
    },

    async revoke(ledgerId, invitationId) {
      await client.call<void, Record<string, never>>({
        method: 'POST',
        path: `${base(ledgerId)}/${encodeURIComponent(invitationId)}/revoke`,
        body: {},
      })
    },

    async accept(token) {
      const response = await client.call<Schemas['LedgerMembershipResponse'], Record<string, never>>({
        method: 'POST',
        path: `/api/v1/invitations/${encodeURIComponent(token)}/accept`,
        body: {},
      })
      return { ledgerId: response.ledgerId, role: response.role, capabilities: response.capabilities }
    },
  }
}

function toInvitation(response: Schemas['LedgerInvitationResponse']): Invitation {
  return {
    id: response.id,
    ledgerId: response.ledgerId,
    invitedSubject: response.invitedSubject,
    role: response.role,
    status: response.status,
    expiresAt: parseInstant(response.expiresAt),
  }
}
